/*
 * Design: Gradient Noir – Reusable service page template
 * Hero → Explanation → Benefits → Process → CTA
 */
import { Link } from "wouter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { LucideIcon, CheckCircle2, ArrowLeft } from "lucide-react";

interface ServicePageProps {
  title: string;
  subtitle: string;
  description: string;
  benefits: string[];
  processSteps: { title: string; desc: string }[];
  icon: LucideIcon;
  image?: string;
  extraHeroButtons?: { label: string; href: string }[];
  heroSide?: React.ReactNode;
}

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function ServicePageTemplate({
  title,
  subtitle,
  description,
  benefits,
  processSteps,
  icon: Icon,
  image,
  extraHeroButtons,
  heroSide,
}: ServicePageProps) {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#3DD5B0]/8 rounded-full blur-[100px]" />

        <div className="container relative z-10">
          <div className={heroSide ? "grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" : ""}>
            <div className={heroSide ? "max-w-2xl" : "max-w-3xl"}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#66CDAA]/10 border border-[#66CDAA]/20 mb-6">
                <Icon className="w-4 h-4 text-[#66CDAA]" />
                <span className="text-sm text-[#66CDAA] font-medium">{subtitle}</span>
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
                {title}
              </h1>
              <p className="text-lg text-white/60 leading-relaxed mb-8 max-w-2xl">
                {description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/contact" className="glow-btn text-base px-8 py-3.5">
                  דברו איתנו
                </Link>
                {extraHeroButtons?.map((btn, i) => (
                  <Link
                    key={i}
                    href={btn.href}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/10 text-white/80 hover:text-white hover:border-white/20 transition-all text-base"
                  >
                    {btn.label}
                    <ArrowLeft className="w-4 h-4" />
                  </Link>
                ))}
                <Link
                  href="/services"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/10 text-white/80 hover:text-white hover:border-white/20 transition-all text-base"
                >
                  כל השירותים
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </div>
            </div>
            {heroSide && (
              <div className="hidden lg:flex items-center justify-center">
                {heroSide}
              </div>
            )}
          </div>
          {heroSide && (
            <div className="lg:hidden mt-16 px-4">{heroSide}</div>
          )}
        </div>
      </section>

      {/* Image + Description */}
      {image && (
        <section className="py-16 lg:py-24 section-navy">
          <div className="container">
            <AnimatedSection>
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <img
                    src={image}
                    alt={title}
                    className="rounded-2xl shadow-2xl shadow-black/30 border border-white/5"
                    loading="lazy"
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <h2 className="text-3xl font-bold text-white mb-6 font-[Rubik]">
                    מה אנחנו מציעים
                  </h2>
                  <p className="text-white/60 leading-relaxed text-lg">
                    {description}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="py-16 lg:py-24 section-dark">
        <div className="container">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12 font-[Rubik] text-center">
              היתרונות שלנו
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="glass-card glass-card-hover p-6 transition-all duration-300 h-[90px] flex items-center">
                  <div className="flex gap-4 items-center">
                    <div className="w-10 h-10 rounded-xl bg-[#66CDAA]/10 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-[#66CDAA]" />
                    </div>
                    <p className="text-white/80 text-base leading-relaxed">
                      {benefit}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 lg:py-24 section-navy">
        <div className="container">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12 font-[Rubik] text-center">
              תהליך העבודה
            </h2>
          </AnimatedSection>
          <div className="max-w-3xl mx-auto space-y-6">
            {processSteps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 150}>
                <div className="flex gap-6 items-start">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#66CDAA] to-[#3DD5B0] flex items-center justify-center shrink-0 text-[#0F172A] font-bold text-lg font-[Rubik]">
                    {i + 1}
                  </div>
                  <div className="pt-1">
                    <h3 className="text-xl font-bold text-white mb-2 font-[Rubik]">
                      {step.title}
                    </h3>
                    <p className="text-white/50 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="container relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-[Rubik]">
              בואו נתקדם יחד
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto">
              רוצים להתחיל? צרו קשר ונבנה יחד את האסטרטגיה הדיגיטלית שלכם.
            </p>
            <Link href="/contact" className="glow-btn inline-block text-lg px-10 py-4">
              דברו איתנו
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
